import React, { type JSX, useState } from 'react';
import {
  Divider,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  SxProps,
  Tooltip,
  styled,
  useTheme,
} from '@mui/material';
import { router, usePage } from '@inertiajs/react';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import HomeIcon from '@mui/icons-material/Home';
import RequestQuote from '@mui/icons-material/RequestQuote';

import { Firm, User } from '@/types';
import { can, firmRoute, isSuperAdmin } from '@/core/utils';
import { useNavigationPermissions } from '@/core/hooks/useNavigationPermissions';
import { Permission } from '@/core/const';
import { isCurrentFirmHarvestEnabled } from '@/pages/firms/utils';
import { useFeature } from '@/core/hooks/useFeature';

export const CollapsedNavigation = (): JSX.Element => {
  const {
    component,
    url,
    props: { auth, firm, firms },
  } = usePage<{
    auth: { user: User };
    firm: Firm | null;
    firms: Firm[];
  }>();

  const { user } = auth;
  const { palette } = useTheme();

  const [manageTaxpayersContactsAnchor, setManageTaxpayersContactsAnchor] =
    useState<null | HTMLElement>(null);

  const [feedbackAnchor, setFeedbackAnchor] = useState<null | HTMLElement>(
    null,
  );

  const [manageUsersRolesAnchor, setManageUsersRolesAnchor] =
    useState<null | HTMLElement>(null);

  const [teamManageTeamsAnchor, setTeamManageTeamsAnchor] =
    useState<null | HTMLElement>(null);

  const {
    canManageUsersRolesTeams,
    canManageAllOrTeamRenewals,
    canTeamManageTeams,
    canManageAllOrTeamInvoices,
    showAdminLabel,
    showStrategyLibrary,
  } = useNavigationPermissions(user);

  const questionnairesEnabled = useFeature('questionnaire_task_type');

  const getIconStyle = (componentNames: string[]): SxProps => {
    if (
      componentNames.some((componentName) =>
        component.startsWith(componentName),
      )
    ) {
      return {
        backgroundColor: '#135D9514',
        color: palette.info.main,
      };
    }
    return {};
  };

  return (
    <List
      component="nav"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        flexGrow: 1,
      }}
    >
      <ListItem sx={{ justifyContent: 'center', position: 'relative' }}>
        <Tooltip title="Engagements" placement="bottom">
          <IconButton
            href={firmRoute('engagements')}
            className={component.startsWith('engagements') ? 'active' : ''}
            aria-label="Engagements"
            sx={getIconStyle(['engagements'])}
          >
            <HomeIcon />
          </IconButton>
        </Tooltip>
      </ListItem>
      <ListItem sx={{ justifyContent: 'center' }}>
        <Tooltip title="Clients" placement="bottom">
          <IconButton
            onClick={(event: React.MouseEvent<HTMLElement>): void =>
              setManageTaxpayersContactsAnchor(event.currentTarget)
            }
            aria-label="Manage contacts and clients"
            sx={getIconStyle(['contacts', 'taxpayers'])}
          >
            <GroupIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={manageTaxpayersContactsAnchor}
          open={!!manageTaxpayersContactsAnchor}
          onClick={(): void => setManageTaxpayersContactsAnchor(null)}
          onClose={(): void => setManageTaxpayersContactsAnchor(null)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {(canTeamManageTeams || canManageUsersRolesTeams) && (
            <MenuItem
              onClick={(): void => {
                setManageTaxpayersContactsAnchor(null);
                router.visit(firmRoute('contacts.index'));
              }}
              sx={getIconStyle(['contacts'])}
            >
              <NavLink aria-label="Contacts">Contacts</NavLink>
            </MenuItem>
          )}
          <MenuItem
            onClick={(): void => {
              setManageTaxpayersContactsAnchor(null);
              router.visit(firmRoute('taxpayers.index'));
            }}
            sx={getIconStyle(['taxpayers'])}
          >
            <NavLink aria-label="Clients">Clients</NavLink>
          </MenuItem>
          {canManageAllOrTeamRenewals && (
            <MenuItem
              onClick={(): void => {
                setManageTaxpayersContactsAnchor(null);
                router.visit(firmRoute('proposals.index'));
              }}
              sx={getIconStyle(['renewals'])}
            >
              <NavLink aria-label="Proposals">Proposals</NavLink>
            </MenuItem>
          )}
        </Menu>
      </ListItem>

      {canManageAllOrTeamInvoices &&
        isCurrentFirmHarvestEnabled(firms, firm?.pid) && (
          <ListItem sx={{ justifyContent: 'center', position: 'relative' }}>
            <Tooltip title="Invoices" placement="bottom">
              <IconButton
                href={firmRoute('invoices.index')}
                className={component.startsWith('invoices') ? 'active' : ''}
                aria-label="Invoices"
                sx={getIconStyle(['invoices'])}
              >
                <RequestQuote />
              </IconButton>
            </Tooltip>
          </ListItem>
        )}

      {(canTeamManageTeams || firm?.isAdvisoryFirm) && (
        <ListItem sx={{ justifyContent: 'center' }}>
          <Tooltip title="Manage" placement="bottom">
            <IconButton
              onClick={(event: React.MouseEvent<HTMLElement>): void =>
                setTeamManageTeamsAnchor(event.currentTarget)
              }
              aria-label="Manage"
              sx={getIconStyle([
                'teams',
                'firms',
                'workflows',
                'advisory-opportunities',
                'strategy-library',
                'questionnaires',
              ])}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Menu
            open={!!teamManageTeamsAnchor}
            anchorEl={teamManageTeamsAnchor}
            onClick={(): void => setTeamManageTeamsAnchor(null)}
            onClose={(): void => setTeamManageTeamsAnchor(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {canTeamManageTeams && (
              <MenuItem
                onClick={(): void => {
                  setTeamManageTeamsAnchor(null);
                  router.visit(firmRoute('firms.show'));
                }}
                sx={getIconStyle(['firms'])}
              >
                <NavLink aria-label="Firm">Firm</NavLink>
              </MenuItem>
            )}
            {showStrategyLibrary && (
              <MenuItem
                onClick={(): void => {
                  setTeamManageTeamsAnchor(null);
                  router.visit(firmRoute('firms.strategy-library.show'));
                }}
                sx={getIconStyle(['strategy-library'])}
              >
                <NavLink aria-label="Strategy Library">
                  Strategy Library
                </NavLink>
              </MenuItem>
            )}
            {/* Internal-only until release: gated to super admins (HWEPD-10110). */}
            {firm?.isAdvisoryFirm && isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setTeamManageTeamsAnchor(null);
                  router.visit(firmRoute('advisory-opportunities.index'));
                }}
                sx={getIconStyle(['advisory-opportunities'])}
              >
                <NavLink aria-label="Advisory Opportunities">
                  Advisory Opportunities
                </NavLink>
              </MenuItem>
            )}
            {firm?.isAdvisoryFirm && (
              <MenuItem
                onClick={(): void => {
                  setTeamManageTeamsAnchor(null);
                  router.visit(firmRoute('workflow-blueprints.index'));
                }}
                sx={
                  component.startsWith('workflows/WorkflowBlueprint') &&
                  !url.startsWith('/admin/')
                    ? getIconStyle(['workflows'])
                    : {}
                }
              >
                <NavLink aria-label="Workflow Templates">
                  Workflow Templates
                </NavLink>
              </MenuItem>
            )}
            {firm?.isAdvisoryFirm && questionnairesEnabled && (
              <MenuItem
                onClick={(): void => {
                  setTeamManageTeamsAnchor(null);
                  router.visit(firmRoute('questionnaire-templates.index'));
                }}
                sx={getIconStyle(['questionnaires'])}
              >
                <NavLink aria-label="Questionnaires">Questionnaires</NavLink>
              </MenuItem>
            )}
          </Menu>
        </ListItem>
      )}

      <ListItem sx={{ justifyContent: 'center', position: 'relative' }}>
        <Tooltip title="Support" placement="bottom">
          <IconButton
            onClick={(event: React.MouseEvent<HTMLElement>): void =>
              setFeedbackAnchor(event.currentTarget)
            }
            aria-label="Support"
            sx={getIconStyle(['feedback'])}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
        <Menu
          open={!!feedbackAnchor}
          anchorEl={feedbackAnchor}
          onClick={(): void => setFeedbackAnchor(null)}
          onClose={(): void => setFeedbackAnchor(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem
            onClick={(): void => {
              setFeedbackAnchor(null);
              window.open(
                'https://help.harnesswealth.com',
                '_blank',
                'noopener,noreferrer',
              );
            }}
          >
            <NavLink aria-label="Help center">Help center</NavLink>
          </MenuItem>
          <MenuItem
            onClick={(): void => {
              setFeedbackAnchor(null);
              router.visit(route('feedback.provide-feedback'));
            }}
            sx={getIconStyle(['feedback/ProvideFeedback'])}
          >
            <NavLink aria-label="Provide feedback">Provide feedback</NavLink>
          </MenuItem>
          <MenuItem
            onClick={(): void => {
              setFeedbackAnchor(null);
              router.visit(route('feedback.report-bug'));
            }}
            sx={getIconStyle(['feedback/ReportBug'])}
          >
            <NavLink aria-label="Report a bug">Report a bug</NavLink>
          </MenuItem>
        </Menu>
      </ListItem>
      {canManageUsersRolesTeams && (
        <ListItem sx={{ justifyContent: 'center' }}>
          <Tooltip
            title={showAdminLabel ? 'Admin' : 'Manage'}
            placement="bottom"
          >
            <IconButton
              onClick={(event: React.MouseEvent<HTMLElement>): void =>
                setManageUsersRolesAnchor(event.currentTarget)
              }
              aria-label="Manage users, roles, teams, and firms"
              sx={getIconStyle([
                'users',
                'roles',
                'teams',
                'admin/firms',
                'admin/taxpayers',
                'admin/contacts',
                'admin/e-file',
                'admin/workflow-measurement',
                'admin/firm-metrics',
                'admin/final-returns',
                'admin/workflow-blueprint-copy',
                'admin/workflow-template-generator',
              ])}
            >
              {showAdminLabel ? <HeadsetMicIcon /> : <SettingsIcon />}
            </IconButton>
          </Tooltip>
          <Menu
            open={!!manageUsersRolesAnchor}
            anchorEl={manageUsersRolesAnchor}
            onClick={(): void => setManageUsersRolesAnchor(null)}
            onClose={(): void => setManageUsersRolesAnchor(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {can(user, Permission.MANAGE_TAXPAYERS) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.taxpayers.index'));
                }}
                sx={getIconStyle(['admin/taxpayers'])}
              >
                <NavLink aria-label="Clients">Clients</NavLink>
              </MenuItem>
            )}
            {can(user, Permission.MANAGE_CONTACTS) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.contacts.index'));
                }}
                sx={getIconStyle(['admin/contacts'])}
              >
                <NavLink aria-label="Contacts">Contacts</NavLink>
              </MenuItem>
            )}
            {can(user, Permission.MANAGE_TAXPAYERS) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.efile'));
                }}
                sx={getIconStyle(['admin/e-file'])}
              >
                <NavLink aria-label="E-file">E-file</NavLink>
              </MenuItem>
            )}
            <Divider />
            {can(user, Permission.MANAGE_TEAMS) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.firms.index'));
                }}
                sx={getIconStyle(['admin/firms'])}
              >
                <NavLink aria-label="Firms">Firms</NavLink>
              </MenuItem>
            )}
            {can(user, Permission.MANAGE_ROLES) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('roles.index'));
                }}
                sx={getIconStyle(['roles'])}
              >
                <NavLink aria-label="Roles">Roles</NavLink>
              </MenuItem>
            )}
            {can(user, Permission.MANAGE_TEAMS) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('teams.index'));
                }}
                sx={getIconStyle(['teams'])}
              >
                <NavLink aria-label="Teams">Teams</NavLink>
              </MenuItem>
            )}
            {can(user, Permission.MANAGE_USERS) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('users.index'));
                }}
                sx={getIconStyle(['users'])}
              >
                <NavLink aria-label="Users">Users</NavLink>
              </MenuItem>
            )}
            {isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.audit-log'));
                }}
                sx={getIconStyle(['admin/audit-log'])}
              >
                <NavLink aria-label="Audit Log">Audit Log</NavLink>
              </MenuItem>
            )}
            {isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.workflow-measurement.index'));
                }}
                sx={getIconStyle(['admin/workflow-measurement'])}
              >
                <NavLink aria-label="Workflow Measurement">
                  Workflow Measurement
                </NavLink>
              </MenuItem>
            )}
            {isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.firm-metrics.index'));
                }}
                sx={getIconStyle(['admin/firm-metrics'])}
              >
                <NavLink aria-label="Firm Metrics">Firm Metrics</NavLink>
              </MenuItem>
            )}
            {isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.final-returns.index'));
                }}
                sx={getIconStyle(['admin/final-returns'])}
              >
                <NavLink aria-label="Final Returns">Final Returns</NavLink>
              </MenuItem>
            )}
            {isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(route('admin.workflow-blueprint-copy.index'));
                }}
                sx={getIconStyle(['admin/workflow-blueprint-copy'])}
              >
                <NavLink aria-label="Copy Workflow Templates">
                  Copy Workflow Templates
                </NavLink>
              </MenuItem>
            )}
            {isSuperAdmin(user.roles) && (
              <MenuItem
                onClick={(): void => {
                  setManageUsersRolesAnchor(null);
                  router.visit(
                    route('admin.workflow-template-generator.index'),
                  );
                }}
                sx={getIconStyle(['admin/workflow-template-generator'])}
              >
                <NavLink aria-label="Generate Workflow Templates">
                  Generate Workflow Templates
                </NavLink>
              </MenuItem>
            )}
          </Menu>
        </ListItem>
      )}
    </List>
  );
};

const NavLink = styled('span')(
  ({ theme }) => `
  color: ${theme.palette.secondary.main};
  font-weight: 400;
  font-size: 0.85rem;
  text-decoration: none;
  display: block;
  padding: 0 0.5rem;
  margin: 0 0 0.1rem 0;

  &:hover {
    opacity: 0.7;
  }
`,
);
